import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Menu, X, ChevronDown, Heart } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/hooks/useWishlist';

const OCCASIONS = [
  { id: 'weddings', label: 'Wedding Gifts' },
  { id: 'baby-showers', label: 'Baby Showers' },
  { id: 'in-loving-memory', label: 'In Loving Memory' },
  { id: 'birthdays', label: 'Birthdays' },
  { id: 'friends', label: 'For Friends' },
  { id: 'holidays', label: 'Holidays' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [occOpen, setOccOpen] = useState(false);
  const [mobileOccOpen, setMobileOccOpen] = useState(false);
  const occRef = useRef(null);
  const { cart } = useCart();
  const { count: wishlistCount } = useWishlist();

  // Close desktop dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (occRef.current && !occRef.current.contains(e.target)) {
        setOccOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const closeAll = () => {
    setIsOpen(false);
    setOccOpen(false);
    setMobileOccOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FAF9F6]/95 backdrop-blur-sm border-b border-[#CAD2C5]/30">
      <div className="container-custom px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <span className="font-serif text-2xl text-[#354F52] tracking-tight">
              Whispering Wristwear
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            <Link to="/products" className="nav-link" data-testid="nav-products">
              Shop
            </Link>

            {/* Shop by Occasion dropdown */}
            <div className="relative" ref={occRef}>
              <button
                onClick={() => setOccOpen((v) => !v)}
                className="nav-link flex items-center gap-1"
                data-testid="nav-occasions"
              >
                Occasions
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${occOpen ? 'rotate-180' : ''}`}
                  strokeWidth={1.5}
                />
              </button>
              {occOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-60 bg-[#FAF9F6] border border-[#CAD2C5]/40 shadow-float rounded-sm py-2 z-50"
                  data-testid="nav-occasions-menu"
                >
                  {OCCASIONS.map((o) => (
                    <Link
                      key={o.id}
                      to={`/collections/${o.id}`}
                      onClick={closeAll}
                      className="block px-5 py-2.5 text-sm text-[#354F52] hover:bg-[#F5F5F0] hover:text-[#D4AF37] transition-colors"
                      data-testid={`nav-occasion-${o.id}`}
                    >
                      {o.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/builder" className="nav-link" data-testid="nav-builder">
              Build Your Own
            </Link>
            <Link to="/custom" className="nav-link" data-testid="nav-custom">
              Custom Order
            </Link>
            <Link to="/blog" className="nav-link" data-testid="nav-blog">
              Blog
            </Link>
            <Link to="/gallery" className="nav-link" data-testid="nav-gallery">
              Gallery
            </Link>
            <Link to="/about" className="nav-link" data-testid="nav-about">
              About
            </Link>
          </div>

          {/* Cart & Mobile Menu */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/wishlist"
              className="relative p-2 hover:bg-[#354F52]/5 rounded-full transition-colors"
              data-testid="nav-wishlist"
              aria-label="Wishlist"
            >
              <Heart className="w-5 h-5 text-[#354F52]" strokeWidth={1.5} />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-white text-xs rounded-full flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </Link>

            <Link
              to="/cart"
              className="relative p-2 hover:bg-[#354F52]/5 rounded-full transition-colors"
              data-testid="nav-cart"
            >
              <ShoppingBag className="w-5 h-5 text-[#354F52]" strokeWidth={1.5} />
              {cart.item_count > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-white text-xs rounded-full flex items-center justify-center">
                  {cart.item_count}
                </span>
              )}
            </Link>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 hover:bg-[#354F52]/5 rounded-full transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="nav-mobile-menu"
            >
              {isOpen ? (
                <X className="w-5 h-5 text-[#354F52]" strokeWidth={1.5} />
              ) : (
                <Menu className="w-5 h-5 text-[#354F52]" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-6 border-t border-[#CAD2C5]/30">
            <div className="flex flex-col gap-4">
              <Link
                to="/products"
                className="nav-link py-2"
                onClick={closeAll}
                data-testid="nav-mobile-products"
              >
                Shop
              </Link>

              {/* Mobile collapsible occasions */}
              <div>
                <button
                  onClick={() => setMobileOccOpen((v) => !v)}
                  className="nav-link py-2 flex items-center justify-between w-full"
                  data-testid="nav-mobile-occasions"
                >
                  <span>Occasions</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${mobileOccOpen ? 'rotate-180' : ''}`}
                    strokeWidth={1.5}
                  />
                </button>
                {mobileOccOpen && (
                  <div className="pl-4 mt-2 flex flex-col gap-2 border-l-2 border-[#CAD2C5]/40">
                    {OCCASIONS.map((o) => (
                      <Link
                        key={o.id}
                        to={`/collections/${o.id}`}
                        onClick={closeAll}
                        className="text-sm text-[#52796F] hover:text-[#D4AF37] py-1.5 transition-colors"
                        data-testid={`nav-mobile-occasion-${o.id}`}
                      >
                        {o.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/builder"
                className="nav-link py-2"
                onClick={closeAll}
                data-testid="nav-mobile-builder"
              >
                Build Your Own
              </Link>
              <Link
                to="/custom"
                className="nav-link py-2"
                onClick={closeAll}
                data-testid="nav-mobile-custom"
              >
                Custom Order
              </Link>
              <Link
                to="/blog"
                className="nav-link py-2"
                onClick={closeAll}
                data-testid="nav-mobile-blog"
              >
                Blog
              </Link>
              <Link
                to="/gallery"
                className="nav-link py-2"
                onClick={closeAll}
                data-testid="nav-mobile-gallery"
              >
                Gallery
              </Link>
              <Link
                to="/about"
                className="nav-link py-2"
                onClick={closeAll}
                data-testid="nav-mobile-about"
              >
                About
              </Link>
              <Link
                to="/wishlist"
                className="nav-link py-2"
                onClick={closeAll}
                data-testid="nav-mobile-wishlist"
              >
                Wishlist {wishlistCount > 0 && <span className="text-[#D4AF37]">({wishlistCount})</span>}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
